import {
  CAP_TO_EM,
  formatSizeLabel,
  HEM_STITCH,
  lineGapInchesFor,
  marginInchesFor,
  PLACARD_COLORS,
  SMALL_TO_LARGE_RATIO,
  VINYL_COLORS,
} from '@cowell/shared';
import { useEffect, useMemo, useState } from 'react';

import { useConfig } from '../state/hooks.js';

// Shared offscreen canvas for text width measurement in the real font.
let measureCtx: CanvasRenderingContext2D | null = null;
function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (measureCtx) return measureCtx;
  measureCtx = document.createElement('canvas').getContext('2d');
  return measureCtx;
}

// Must match the letter-spacing applied on the SVG <text> below.
const LETTER_SPACING_EM = 0.02;

function measureTextWidth(text: string, fontPx: number): number {
  const ctx = getMeasureCtx();
  if (!ctx || !text) return 0;
  ctx.font = `900 ${fontPx}px 'Inter', 'Arial Black', sans-serif`;
  const raw = ctx.measureText(text).width;
  // Canvas measureText returns advance width without CSS letter-spacing.
  // SVG renders with letterSpacing: 0.02em, so add (n-1) gaps of 0.02em.
  const spacing = Math.max(0, text.length - 1) * LETTER_SPACING_EM * fontPx;
  return raw + spacing;
}

interface LineLayout {
  readonly cap: number; // cap height in viewBox units (= inches × 100)
  readonly em: number; // font-size in viewBox units
  readonly naturalWidth: number; // width at `em` in viewBox units
  readonly scaleX: number; // horizontal squish factor (1 = none)
  readonly text: string;
  readonly isEmpty: boolean;
}

export function PreviewPlaceholder() {
  const config = useConfig();
  const placard = PLACARD_COLORS.find((c) => c.id === config.placardColorId);
  const vinyl = VINYL_COLORS.find((c) => c.id === config.vinylColorId);

  // Remeasure after custom Inter Black loads so widths are accurate.
  const [fontTick, setFontTick] = useState(0);
  useEffect(() => {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts?.load) return;
    fonts
      .load('900 100px "Inter"')
      .then(() => setFontTick((t) => t + 1))
      .catch(() => setFontTick((t) => t + 1));
  }, []);

  const layout = useMemo(() => {
    const S = 100; // viewBox units per inch
    const { width: wIn, height: hIn } = config.size;
    const vbW = wIn * S;
    const vbH = hIn * S;

    const margin = marginInchesFor(config.size) * S;
    const innerW = Math.max(0, vbW - 2 * margin);
    const innerH = Math.max(0, vbH - 2 * margin);
    const gap = lineGapInchesFor(config.size) * S;

    const hemInset = HEM_STITCH.inset * S;

    const n = config.lines.length;
    const weights = config.lines.map((l) => (l.size === 'large' ? 1 : SMALL_TO_LARGE_RATIO));
    const totalWeight = weights.reduce((s, w) => s + w, 0) || 1;
    const totalGap = Math.max(0, n - 1) * gap;
    const perUnit = Math.max(0, (innerH - totalGap) / totalWeight);

    const lines: LineLayout[] = config.lines.map((line, i) => {
      const isEmpty = !line.text;
      const text = line.text || `LINE ${i + 1}`;
      const cap = weights[i]! * perUnit;
      const em = cap / CAP_TO_EM;
      const naturalWidth = measureTextWidth(text, em);

      if (em <= 0 || innerW <= 0 || naturalWidth <= 0) {
        return { cap, em, naturalWidth, scaleX: 1, text, isEmpty };
      }

      // Multi-line large: stretch OR squish to fill innerW exactly.
      if (n > 1 && line.size === 'large') {
        return { cap, em, naturalWidth, scaleX: innerW / naturalWidth, text, isEmpty };
      }

      // All other cases: keep natural if it fits, otherwise squish horizontally.
      // Cap height stays at target — never shrink the letters vertically.
      if (naturalWidth <= innerW) {
        return { cap, em, naturalWidth, scaleX: 1, text, isEmpty };
      }
      return { cap, em, naturalWidth, scaleX: innerW / naturalWidth, text, isEmpty };
    });

    const contentH =
      lines.reduce((s, l) => s + l.cap, 0) + Math.max(0, n - 1) * gap;

    return { vbW, vbH, margin, innerH, hemInset, gap, lines, contentH };
    // fontTick dep forces remeasure when the real Inter Black finishes loading.
  }, [config, fontTick]);

  const { width, height } = config.size;
  const { vbW, vbH, margin, innerH, hemInset, gap, lines, contentH } = layout;

  let cursorTop = margin + (innerH - contentH) / 2;

  return (
    <div className="preview">
      <svg
        viewBox={`0 0 ${vbW} ${vbH}`}
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: 'block',
          width: `min(100%, calc(var(--preview-max-h) * ${width / height}))`,
          height: 'auto',
          maxHeight: 'var(--preview-max-h)',
          filter: 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.18))',
        }}
        aria-label={`Preview of ${formatSizeLabel(config.size)} patch`}
      >
        {/* Placard base */}
        <rect x={0} y={0} width={vbW} height={vbH} fill={placard?.hex ?? '#333'} />

        {/* Hem stitch: dashed rect inset by hemInset */}
        <rect
          x={hemInset}
          y={hemInset}
          width={vbW - hemInset * 2}
          height={vbH - hemInset * 2}
          fill="none"
          stroke={placard?.hex ?? '#333'}
          strokeWidth={1.5}
          strokeDasharray="8 5"
          strokeOpacity={0.55}
          style={{ filter: 'brightness(0.7)' }}
        />

        {/* Text lines */}
        {lines.map((line, i) => {
          const centerX = vbW / 2;
          const baselineY = cursorTop + line.cap;
          cursorTop = baselineY + gap;

          const common = {
            y: baselineY,
            fontSize: line.em,
            fontFamily: "'Inter', 'Helvetica', 'Arial Black', sans-serif",
            fontWeight: 900,
            textAnchor: 'middle' as const,
            fill: vinyl?.hex ?? '#fff',
            opacity: line.isEmpty ? 0.28 : 1,
            style: { letterSpacing: '0.02em' },
          };

          if (line.scaleX !== 1) {
            // Anchor the scale at centerX so the text stays centered.
            return (
              <text
                key={i}
                {...common}
                x={centerX}
                transform={`translate(${centerX} 0) scale(${line.scaleX} 1) translate(${-centerX} 0)`}
              >
                {line.text}
              </text>
            );
          }
          return (
            <text key={i} {...common} x={centerX}>
              {line.text}
            </text>
          );
        })}
      </svg>

      <div className="preview__label">
        {formatSizeLabel(config.size)} — placeholder, PixiJS renderer next commit
      </div>
    </div>
  );
}
